import { UPDATE_PRIORITY, type Texture } from "pixi.js";
import { shapeHintFromLaunchContext } from "./launch/shapeHintFromLaunchContext";
import type { BootConfig } from "./BootConfig";
import type { GameDefinition, GameSceneHandle } from "../composition/GameDefinition";
import type { RoundLifecycle } from "../composition/RoundLifecycle";
import type { BootLaunchPayload } from "./BootLaunchPayload";
import type { GameFixturesDefinition } from "../composition/GameDefinition";
import type { ReplayRoundProvider } from "../replay/ReplayRoundProvider";
import { createSlotApp } from "../app/createSlotApp";
import { installDebugHook } from "../dev/installDebugHook";
import { installRuntimeDebugHook } from "../dev/installRuntimeDebugHook";
import { createStage } from "./createStage";
import { installDevTools, loadDevMenu } from "./installDevTools";
import { DomBootLoader } from "./loader/DomBootLoader";
import { bootGame } from "./bootGame";
import { DeferredFeatureHudPort } from "../feature-hud/DeferredFeatureHudPort";
import { createCoreServices } from "./createCoreServices";
import { BackendReplayRoundProvider } from "../replay/BackendReplayRoundProvider";
import { fetchReplayRound, playReplayRound, showReplayBet } from "./flows/replayRound";
import { finishBoot } from "./flows/finishBoot";
import { createShellChrome } from "./createShellChrome";
import { resolveChromeContentArea } from "../chrome/chromeContentArea";
import { createRuntime } from "./createRuntime";
import { CanvasTapRegistry } from "../spin/CanvasTapTargets";
import { PlayerSpinGate } from "../spin/PlayerSpinGate";
import { BetLinesService } from "../bet/BetLinesService";
import { DeferredFeatureOfferPort } from "../feature-purchase/DeferredFeatureOfferPort";
import { createControlBar } from "./createControlBar";
import { restorePendingRound, type RestoreState } from "./flows/restorePendingRound";
import { runIntro, stageTitleIntro } from "./flows/runIntro";
import { SHELL_BUNDLE_NAMES } from "./defaults/BundleNames";
import { BootBoundary } from "./failure/BootBoundary";
import { BrandedIntroSplashPresenter } from "../intro/BrandedIntroSplashPresenter";
import { NO_INTRO_CARDS } from "../intro/IntroSplashSpec";
import { mountStillBackground } from "../background/mountStillBackground";
import { ContentAreaStore, PixiPresentationTicker, PresentationTimerDriver, SpineDriver, tagLog } from "@slot/presentation";
import type { Log } from "@slot/presentation";

/**
 * The boot order every title shares, run by `launchGame` (plan §2.2): boots `definition` into `host` —
 * app → boot payload → services → scene → chrome → presentation → runtime → control bar → dev hook →
 * intro / restore / replay. Each stage is a factory; the game contributes through the definition only.
 * The config comes as a factory the boundary runs (`createConfig`, the shell's `createBootConfig`), so
 * a build that is refused (a `deployed` build that names no URL) shows the boot-failure popup like any
 * startup failure (decision 31).
 */
export async function startGame<TScene extends GameSceneHandle>(host: HTMLElement, definition: GameDefinition<TScene>, createConfig: () => BootConfig): Promise<void> {
  const sink: Log = (message) => console.log(message);
  const log = tagLog(sink, "Boot");
  // The boot boundary first, so a failure anywhere below (the game's config and the renderer included) reaches the popup.
  const boundary = new BootBoundary({ host, log, reload: () => window.location.reload() });
  try {
    await runBoot(host, definition, createConfig, sink, log, boundary);
  } catch (error) {
    boundary.fail(error);
  }
}

async function runBoot<TScene extends GameSceneHandle>(host: HTMLElement, definition: GameDefinition<TScene>, createConfig: () => BootConfig, sink: Log, log: Log, boundary: BootBoundary): Promise<void> {
  // The loader first: index.html carries its markup, so it painted before this bundle arrived. It is
  // adopted rather than created (decision 19) and handed to the boundary before anything can fail,
  // since it paints over the popup. `?skipLoader=1` drops it at once.
  const loader = DomBootLoader.adopt();
  if (loader) boundary.attachBootLoader(() => loader.dismiss());
  const config = createConfig();
  if (config.skipLoader) loader?.dismiss();
  const slot = await createSlotApp({ host, background: 0x0b0f14, preference: "webgl", transparent: definition.background?.draw === "dom" });
  const hook = config.devModeEnabled ? installDebugHook({ version: definition.version, app: slot.app, viewport: slot.viewport }) : null;
  const layers = createStage(slot);
  const presentationTicker = new PixiPresentationTicker(slot.app.ticker);
  const devTools = installDevTools({ slot, presentationTicker, devModeEnabled: config.devModeEnabled, log });

  // Boot: settings, launch context, bundles, branding; the loader reports meanwhile.
  const { payload } = await bootGame({ config, game: definition.boot, background: definition.background, log, loader });
  // The page speaks the active language (screen readers, hyphenation); until 2026-09-26 it said "en" whatever was active (review finding 85).
  document.documentElement.lang = payload.localization.getLocale();

  // Clocks + services. `?replay=<id>`: a zero balance, the bet locked for good, the round fetched BEFORE
  // the control bar renders so the bet panel never flashes the boot default.
  slot.viewport.setShapeHint(shapeHintFromLaunchContext(payload.launchContext));
  const contentArea = new ContentAreaStore({ viewport: slot.viewport, rule: resolveChromeContentArea });
  const spineDriver = new SpineDriver(presentationTicker);
  const featureHud = new DeferredFeatureHudPort();
  const replayMode = payload.replayContext !== null;
  const replayRounds = createReplayRoundProvider(payload, definition.fixtures);
  const replay = payload.replayContext ? await fetchReplayRound(payload.replayContext.roundId, replayRounds, loader, log) : null;
  // A replay's money is in the round's currency (decision 81), when the catalog knows it: the viewer's settings are anonymous.
  const roundCurrency = replay?.spinResponse?.currencyCode;
  const { services, devRecorders } = createCoreServices({
    payload,
    log: tagLog(sink, "Game"),
    presentationTimerDriver: new PresentationTimerDriver(presentationTicker),
    speedModeScales: definition.speedModeScales,
    featureHud,
    gameAudioOverrides: definition.boot.audioOverrides,
    denominationFactor: config.denominationFactor,
    betLockReason: replayMode ? "replay_mode" : undefined,
    initialBalance: replayMode ? "0" : undefined,
    currencyCode: roundCurrency && payload.currencyCatalog[roundCurrency] ? roundCurrency : undefined,
  });
  // The game's files are in; the sounds download while the scene is built and the intro waits for
  // its tap, so they are ready when the player gets there (user, 2026-09-26).
  services.audioService.control.preloadSounds();
  if (replay) showReplayBet(replay, services);
  log(`services ready: currency=${services.moneyContext.currencyCode} bet=${services.betSystem.formatCurrentBet()} shape=${slot.viewport.getSnapshot().shape} brand=${payload.brandedAssets.getOperatorId() ?? "none"}`);

  // Dev mode only, like the hook (decision 30): a build that ignores `?dev=1` reaches no shader scene.
  const filterScene = config.devModeEnabled && new URLSearchParams(window.location.search).get("filters") === "1" ? definition.dev?.createFilterTestScene?.(payload, slot) : undefined;
  if (filterScene) {
    layers.game.addChild(filterScene.root);
    slot.viewport.subscribe(filterScene.layout);
    finishBoot(services, loader, slot, hook);
    return;
  }

  // The title's still under the scene (decision 69): in the canvas, its first decoded before the scene is built.
  if (definition.background) await mountStillBackground({ background: definition.background, assets: payload.assets, slot, layers, log });
  const backgroundStillOnly = config.devModeEnabled && new URLSearchParams(window.location.search).get("background") === "still";
  // The scene (the game's art), the shell chrome around it, the round presentation, the runtime.
  const scene = definition.createScene({ payload, services, viewport: slot.viewport, contentArea, presentationTicker, spineDriver, renderer: slot.app.renderer, backgroundRoot: slot.backgroundRoot, backgroundStillOnly, log });
  layers.game.addChild(scene.node);
  if (scene.modalsNode) layers.modals.addChild(scene.modalsNode);
  // Boot is a canvas overlay — the loader, then the intro splash (both DOM, decision 19): the bar
  // family stays hidden until the game is ready (decision 18).
  const releaseBootCover = services.canvasOverlayStore.open();
  const chrome = createShellChrome({ slot, layers, payload, services, presentationTicker, featureHud, showFeatureHud: definition.chrome.featureHud !== false, compositionRoot: scene.compositionRoot, compositionSize: scene.compositionSize, logo: scene.logo, background: definition.background, theme: definition.chrome.theme, controlArt: definition.chrome.controlArt, barLayout: definition.chrome.barLayout, log });
  boundary.attachSystemModal(chrome.systemModalPresenter, (key, fallback) => services.localization.t(key, fallback));
  const fixtureProvider = payload.backendConfig.baseUrl.length > 0 ? null : definition.fixtures.createRoundProvider();
  // "After draw": listeners below Pixi's render priority run once the frame has been rendered.
  const waitForNextRenderedFrame = (): Promise<void> => new Promise<void>((resolve) => slot.app.ticker.addOnce(() => resolve(), undefined, UPDATE_PRIORITY.LOW - 1));
  // Settlement + feature buys for the presenters: closures over the runtime created right after (only
  // ever called after boot). Absent in replay mode: a replayed round is never settled or bought from.
  const lifecycle: RoundLifecycle | null = replayMode
    ? null
    : {
        acknowledgePresentationComplete: (roundIdentifier) => void runtime.acknowledgePresentationCompleteOnce(roundIdentifier),
        buyFeature: (featureId) => runtime.buyFeatureWhenIdle(featureId),
      };
  // The title's canvas taps and its way to one offer's confirm (decision 51), bound when the control bar is built.
  const canvasTaps = new CanvasTapRegistry();
  const featureOffers = new DeferredFeatureOfferPort();
  // The title's way to pace a round by the player's Spin (decision 55); a replay has no Spin button: every wait passes.
  const playerSpins = new PlayerSpinGate();
  if (replayMode) playerSpins.setPassing(true);
  // The title's way to the bet's lines (decision 56), bound to the coordinator when the control bar is built.
  const betLines = new BetLinesService({ betSystem: services.betSystem, autoSpinStore: services.autoSpinStore, featureStateStore: services.featureStateStore, audioService: services.audioService, replayMode });
  const presentation = definition.createPresentation(scene, { payload, services, grid: definition.grid, spineDriver, lifecycle, waitForNextRenderedFrame, replayMode, canvasTaps, featureOffers, playerSpins, betLines, log });
  const runtime = createRuntime({ payload, services, presentation, bootFallbackGrid: definition.bootFallbackGrid, fixtureProvider, onShowSystemModal: (modal) => chrome.systemModalPresenter.show(modal), log });
  await createControlBar({ slot, payload, services, chrome, runtime, gameChrome: definition.chrome, fixtures: definition.fixtures, replayMode, canvasTaps, featureOffers, playerSpins, betLines, log });

  // An interrupted round the settings report (never in replay mode): `pendingRoundId`, or a `pendingRound` the server is
  // already paying, which comes without one (decision 79).
  const pendingRound = replayMode ? null : payload.initialSettings.pendingRound ?? null;
  const pendingRoundId = replayMode ? null : payload.initialSettings.pendingRoundId ?? (pendingRound?.state === "settling" ? pendingRound.roundIdentifier : null);
  const restore: RestoreState | null = pendingRoundId ? { status: "fetching", roundId: pendingRoundId } : null;
  if (hook) {
    const debugHook = installRuntimeDebugHook({ hook, runtime, deps: services, presentationTicker, offlineRounds: fixtureProvider, stage: slot.app.stage, devRecorders, playerSpins, betLines, getReplayStatus: () => replay?.status ?? null, getRestoreStatus: () => restore?.status ?? null });
    definition.dev?.installHook?.({ hook: debugHook, services, roundProvider: fixtureProvider, stage: slot.app.stage });
  }
  if (devTools) {
    const gameSections = definition.dev?.createMenuSections?.({ runtime, services, roundProvider: fixtureProvider }) ?? [];
    await loadDevMenu({ devTools, slot, presentationTicker, spinCoordinator: runtime.getSpinCoordinator(), services, offlineRounds: fixtureProvider, backendBaseUrl: payload.backendConfig.baseUrl, gameSections, log });
  }
  await runtime.start();

  // Intro: splash → loader off → gesture → audio unlock → pending-round restore → bet unlock. A replay
  // that failed to fetch has nothing to show behind the splash: straight to the error modal.
  const introPresenter = new BrandedIntroSplashPresenter(chrome.introSplash, definition.chrome.introSplash ?? NO_INTRO_CARDS, payload.gameVersion, payload.initialSettings.minAgeYears ?? null, services.localization, chrome.resolveIntroCardIcon);
  await Promise.all([introPresenter.applySpec(), chrome.introArtReady]);
  // The title's own intro (decision 77), drawn in the canvas from its scene's art: the splash keeps its chrome only.
  const titleIntro = definition.createIntro
    ? stageTitleIntro(
        definition.createIntro(scene, {
          viewport: slot.viewport,
          runner: services.chromeTaskRunner,
          localization: services.localization,
          logo: payload.assets.getLoadedAssetOrNull<Texture>(SHELL_BUNDLE_NAMES.bootAssets, "logo"),
          log: tagLog(sink, "Intro"),
        }),
        { scene: scene.node, layer: layers.intro },
      )
    : null;
  if (titleIntro) chrome.introSplash.setContentShown(false);
  await runIntro(chrome.introSplash, services, { skipIntro: payload.skipIntro || replay?.status === "fetch_failed", fastIntro: replayMode, dismissLoader: () => loader?.dismiss(), titleIntro });
  if (restore) {
    // The splash already dropped the loader; when the intro was skipped it is still up and would cover the modal.
    loader?.dismiss();
    // The restore plays under the boot cover, the bar hidden: no Spin to press, so its round passes every wait.
    playerSpins.setPassing(true);
    await restorePendingRound(restore, pendingRound, replayRounds, runtime, services, chrome.systemModalPresenter, () => window.location.reload(), log);
    playerSpins.setPassing(false);
  }
  finishBoot(services, loader, slot, hook, { unlockBet: !replayMode });
  releaseBootCover();
  if (replay) await playReplayRound(replay, runtime, services, chrome.systemModalPresenter, () => window.location.reload(), log);
}

/** The backend replay endpoint when a backend is configured, else the game's offline fixture rounds (replay + restore). */
function createReplayRoundProvider(payload: BootLaunchPayload, fixtures: GameFixturesDefinition): ReplayRoundProvider {
  return payload.backendConfig.baseUrl.length > 0
    ? new BackendReplayRoundProvider({ baseUrl: payload.backendConfig.baseUrl, replayPath: payload.backendConfig.replayPath })
    : fixtures.createReplayRoundProvider();
}
